import classNames from 'classnames';
import React, { useState } from 'react';
import {
  formatDateTime,
  formatDurationHoursShort,
  formatShortenedTimespan,
  formatTimeAgo,
  formatTimespan,
} from '../common/formatHelper';
import Loader from '../components/loaders/Loader';

export type DurationType = 'default' | 'short' | 'hours' | 'hoursShort';

export type TimeDateProps = {
  children?: React.ReactNode;
  className?: string;
  updatedDate?: Date | null;
  createdDate?: Date | null;
  timeDate?: Date | null;
  isLoading?: boolean;
  alignEnd?: boolean;
  alignStart?: boolean;
  showDuration?: boolean; // Whether to show duration between createdDate and updatedDate
  durationType?: DurationType; // Format type for the duration
  t?: any; // Translation function for formatDurationHoursShort
};

// Helper to validate if a date is valid
const isValidDate = (date?: Date | null): boolean => {
  return Boolean(date && date instanceof Date && !isNaN(date.getTime()));
};

// Safely get timestamp from date
const safeGetTime = (date?: Date | null): number => {
  if (!isValidDate(date)) return 0;
  try {
    return date!.getTime();
  } catch (error) {
    console.error('Error getting timestamp from date:', error);
    return 0;
  }
};

// Safely format date with fallback
const safeFormatDateTime = (date?: Date | null): string => {
  if (!isValidDate(date)) return 'N/A';
  try {
    return formatDateTime(date!);
  } catch (error) {
    console.error('Error formatting date time:', error);
    return 'N/A';
  }
};

// Safely get locale string with fallback
const safeToLocaleString = (date?: Date | null): string => {
  if (!isValidDate(date)) return 'N/A';
  try {
    return date!.toLocaleString();
  } catch (error) {
    console.error('Error getting locale string from date:', error);
    return 'N/A';
  }
};

const TimeAgo: React.FC<TimeDateProps> = props => {
  const [showTimeAgo, setShowTimeAgo] = useState(true);

  const toggleFormat = () => setShowTimeAgo(!showTimeAgo);

  // Format the duration between created and updated dates if both exist
  const formatDuration = (): string => {
    if (!isValidDate(props.createdDate) || !isValidDate(props.updatedDate)) {
      return '';
    }

    try {
      const createdTime = safeGetTime(props.createdDate);
      const updatedTime = safeGetTime(props.updatedDate);

      // Make sure updatedTime is after createdTime to avoid negative durations
      if (updatedTime <= createdTime) {
        return '';
      }

      switch (props.durationType) {
        case 'short':
          return formatShortenedTimespan(updatedTime, createdTime);
        case 'hours':
        // return formatDurationHours(updatedTime, createdTime);
        case 'hoursShort':
          return props.t
            ? formatDurationHoursShort(props.t, updatedTime, createdTime)
            : formatShortenedTimespan(updatedTime, createdTime); // Fallback if no t function
        default:
          return formatTimespan(updatedTime, createdTime);
      }
    } catch (error) {
      console.error('Error calculating duration:', error);
      return '';
    }
  };

  // Generate display date safely
  const getDisplayDate = (): string => {
    if (!isValidDate(props.timeDate)) {
      return '';
    }

    try {
      return showTimeAgo
        ? formatTimeAgo(props.timeDate!, {
            includeSeconds: false,
            includeMinutes: true,
          })
        : safeToLocaleString(props.timeDate);
    } catch (error) {
      console.error('Error generating display date:', error);
      return '';
    }
  };

  const displayDate = getDisplayDate();

  // If showing duration, get the formatted duration string
  const displayDuration = props.showDuration ? formatDuration() : null;

  return (
    <div
      data-component="TimeAgo"
      title={`Updated: ${safeFormatDateTime(props.updatedDate)} \nCreated: ${safeFormatDateTime(props.createdDate)}`}
      aria-label={`Updated: ${safeToLocaleString(props.updatedDate)} ${'\n'} Created: ${safeToLocaleString(props.createdDate)}`}
      className={classNames(
        'flex-1 text-xs',
        'opacity-75',
        props.alignStart ? 'text-start' : 'text-end',
        props.className,
      )}
      onClick={toggleFormat}
      style={{ cursor: 'pointer' }}
    >
      {displayDate}
      {displayDuration && displayDuration !== '0sec' && (
        <span className="text-brand-muted mx-1 text-xs">
          ({displayDuration})
        </span>
      )}
      {props.children}
      {props.isLoading && (
        <Loader type={'loading-original'} size={20} stroke={10} />
      )}
    </div>
  );
};

export default TimeAgo;
